import React from "react";

/**
 * PrimaryColorContext - Manages the app's primary/brand color selection.
 *
 * Stores a Tailwind color name (e.g. "teal", "sky", "indigo`) in localStorage.
 * Components construct Tailwind classes dynamically using the color name:
 *   `bg-${primaryColor}-600`, `text-${primaryColor}-800`, etc.
 *
 * All 16 brand colors are safelisted in tailwind.config.js so dynamically
 * constructed classes are never stripped at compile time.
 *
 * @author Forge + opencode (glm-5.1)
 * @version 2.0
 * @since 2026-04-17
 */

/** The 16 available brand colors */
export const BRAND_COLORS = [
  "slate", "red", "orange", "amber", "yellow", "lime", "green",
  "emerald", "teal", "cyan", "sky", "blue", "indigo", "violet", "purple", "pink",
];

/** Display labels for each brand color */
export const COLOR_LABELS = {
  slate: "Slate",
  red: "Red",
  orange: "Orange",
  amber: "Amber",
  yellow: "Yellow",
  lime: "Lime",
  green: "Green",
  emerald: "Emerald",
  teal: "Teal",
  cyan: "Cyan",
  sky: "Sky",
  blue: "Blue",
  indigo: "Indigo",
  violet: "Violet",
  purple: "Purple",
  pink: "Pink",
};

const DEFAULT_COLOR = "teal";
const STORAGE_KEY = "htx-primary-color";

const PrimaryColorContext = React.createContext({
  primaryColor: DEFAULT_COLOR,
  setPrimaryColor: () => {},
});

/**
 * PrimaryColorProvider - Wraps the app and provides primary color state.
 *
 * Reads the saved color from localStorage on mount and persists changes.
 * No CSS variables are set — components use Tailwind classes directly.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 *
 * @author Forge + opencode (glm-5.1)
 * @version 2.0
 * @since 2026-04-17
 */
export function PrimaryColorProvider({ children }) {
  const [primaryColor, setPrimaryColorState] = React.useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && BRAND_COLORS.includes(saved)) return saved;
      return DEFAULT_COLOR;
    } catch {
      return DEFAULT_COLOR;
    }
  });

  /**
   * Sets the primary color and persists it to localStorage.
   *
   * @param {string} colorName - A key from BRAND_COLORS
   */
  const setPrimaryColor = React.useCallback((colorName) => {
    if (BRAND_COLORS.includes(colorName)) {
      setPrimaryColorState(colorName);
      try {
        localStorage.setItem(STORAGE_KEY, colorName);
      } catch {
        // localStorage unavailable
      }
    }
  }, []);

  const value = React.useMemo(
    () => ({ primaryColor, setPrimaryColor }),
    [primaryColor, setPrimaryColor]
  );

  return (
    <PrimaryColorContext.Provider value={value}>
      {children}
    </PrimaryColorContext.Provider>
  );
}

/**
 * Hook to access the primary color context.
 *
 * @returns {{ primaryColor: string, setPrimaryColor: (colorName: string) => void }}
 *
 * @author Forge + opencode (glm-5.1)
 * @version 2.0
 * @since 2026-04-17
 */
export function usePrimaryColor() {
  const context = React.useContext(PrimaryColorContext);
  if (!context) {
    throw new Error("usePrimaryColor must be used within a PrimaryColorProvider");
  }
  return context;
}

export default PrimaryColorContext;
